import { useContextSelector } from 'use-context-selector';

import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';

import {
  AmbientIntelligenceActionsContext,
  AmbientIntelligenceStateContext,
} from '../providers/AmbientIntelligenceContext';
import {
  AmbientData,
  AmbientMode,
  CommunicationState,
  ContentItem,
  NudgeState,
} from '../types/ambient-intelligence.types';

export interface UseAmbientIntelligenceReturn {
  // Full state object
  state: {
    isEnabled: boolean;
    isLoading: boolean;
    currentRoute: string | null;
    suggestions: ContentItem[];
    activeContent: ContentItem | null;
    sessionId: string;
    showNudge: boolean;
    showCards: boolean;
    currentMode: AmbientMode;
    nudge: NudgeState;
    communication: CommunicationState;
    suggestion: {
      scrollPosition: number;
    };
    genericContext?: any;
  };

  // State (backward compatibility)
  isEnabled: boolean;
  isLoading: boolean;
  suggestions: ContentItem[];
  activeContent: ContentItem | null;
  showNudge: boolean;
  showCards: boolean;
  currentMode: AmbientMode;
  nudge: NudgeState;
  communication: CommunicationState;
  showGuestLogin: boolean;
  isAuthenticated: boolean;
  sessionId: string;

  // Actions (backward compatibility)
  pushData: (data: AmbientData) => void;
  clearData: () => void;
  inspectContent: (content: ContentItem) => void;
  dismissSuggestions: () => void;
  addCard: (card: ContentItem, options?: { showCards?: boolean }) => void;
  setShowNudge: (show: boolean) => void;
  setShowCards: (show: boolean) => void;
  setShowGuestLogin: (show: boolean) => void;

  // All actions grouped
  actions: {
    pushData: (data: AmbientData) => void;
    clearData: () => void;
    inspectContent: (content: ContentItem) => void;
    dismissSuggestions: () => void;
    addCard: (card: ContentItem, options?: { showCards?: boolean }) => void;
    setShowNudge: (show: boolean) => void;
    setShowCards: (show: boolean) => void;
    setShowGuestLogin: (show: boolean) => void;
    switchMode: (mode: AmbientMode) => void;
    updateNudgeBadge: (count: number | null) => void;
    setNudgeMode: (mode: AmbientMode) => void;
    setUnreadCounts: (counts: CommunicationState['unreadCounts']) => void;
    saveDraftMessage: (threadId: string, draft: string) => void;
    clearDraftMessage: (threadId: string) => void;
    setActiveThread: (threadId: string | null) => void;
    setPendingMessage: (message: CommunicationState['pendingMessage']) => void;
  };
}

export const useAmbientIntelligence = (): UseAmbientIntelligenceReturn => {
  // Get state
  const state = useContextSelector(AmbientIntelligenceStateContext, (state) => state);

  // Get actions
  const actions = useContextSelector(AmbientIntelligenceActionsContext, (actions) => actions);

  // Get auth state
  const auth = useContextSelector(AppContext, authSelector);

  return {
    // Full state object
    state: state,

    // State (backward compatibility)
    isEnabled: state.isEnabled,
    isLoading: state.isLoading,
    suggestions: state.suggestions,
    activeContent: state.activeContent,
    showNudge: state.showNudge,
    showCards: state.showCards,
    currentMode: state.currentMode,
    nudge: state.nudge,
    communication: state.communication,
    showGuestLogin: state.communication.showGuestLogin,
    isAuthenticated: !!auth?.loggedIn,
    sessionId: state.sessionId,

    // Actions (backward compatibility)
    pushData: actions.pushData,
    clearData: actions.clearData,
    inspectContent: actions.inspectContent,
    dismissSuggestions: actions.dismissSuggestions,
    addCard: actions.addCard,
    setShowNudge: actions.setShowNudge,
    setShowCards: actions.setShowCards,
    setShowGuestLogin: actions.setShowGuestLogin,

    // All actions grouped
    actions: {
      pushData: actions.pushData,
      clearData: actions.clearData,
      inspectContent: actions.inspectContent,
      dismissSuggestions: actions.dismissSuggestions,
      addCard: actions.addCard,
      setShowNudge: actions.setShowNudge,
      setShowCards: actions.setShowCards,
      setShowGuestLogin: actions.setShowGuestLogin,
      switchMode: actions.switchMode,
      updateNudgeBadge: actions.updateNudgeBadge,
      setNudgeMode: actions.setNudgeMode,
      setUnreadCounts: actions.setUnreadCounts,
      saveDraftMessage: actions.saveDraftMessage,
      clearDraftMessage: actions.clearDraftMessage,
      setActiveThread: actions.setActiveThread,
      setPendingMessage: actions.setPendingMessage,
    },
  };
};

// Hook for just the state (optimized for components that only read)
export const useAmbientIntelligenceState = () => {
  return useContextSelector(AmbientIntelligenceStateContext, (state) => state);
};

// Hook for just the actions (optimized for components that only dispatch)
export const useAmbientIntelligenceActions = () => {
  return useContextSelector(AmbientIntelligenceActionsContext, (actions) => actions);
};

// Hook for mode-specific functionality
export const useAmbientMode = () => {
  const currentMode = useContextSelector(AmbientIntelligenceStateContext, (state) => state.currentMode);
  const nudge = useContextSelector(AmbientIntelligenceStateContext, (state) => state.nudge);
  const switchMode = useContextSelector(AmbientIntelligenceActionsContext, (actions) => actions.switchMode);

  return {
    currentMode,
    nudge,
    switchMode,
    isSuggestionMode: currentMode === 'suggestion',
    isCommunicationMode: currentMode === 'communication',
  };
};

// Hook for draft messages
export const useDraftMessage = (threadId: string | null) => {
  const draftMessages = useContextSelector(
    AmbientIntelligenceStateContext,
    (state) => state.communication.draftMessages,
  );
  const saveDraftMessage = useContextSelector(
    AmbientIntelligenceActionsContext,
    (actions) => actions.saveDraftMessage,
  );
  const clearDraftMessage = useContextSelector(
    AmbientIntelligenceActionsContext,
    (actions) => actions.clearDraftMessage,
  );

  const draft = threadId ? draftMessages[threadId] || '' : '';

  return {
    draft,
    saveDraft: (message: string) => threadId && saveDraftMessage(threadId, message),
    clearDraft: () => threadId && clearDraftMessage(threadId),
  };
};
